/**
 * The privacy policy. Every claim here is checked against what the code actually does — if a
 * behaviour changes (a new third party, a new stored field, a new cookie), this page changes in
 * the same commit or it becomes a false statement to the public.
 */
import { pageTitle } from '@thottbot/shared';
import { Link } from 'react-router';
import { CONTACT_EMAIL, POLICY_UPDATED, PRIVACY_EMAIL } from './contact';

export function Privacy() {
  return (
    <div className="page legal" data-testid="privacy-page">
      <title>{pageTitle('Privacy policy')}</title>
      <h1>Privacy policy</h1>
      <p className="muted">Last updated {POLICY_UPDATED}.</p>

      <p>
        Thottbot Forever is a fan-run database for the World of Warcraft: Forever client. This
        page describes everything the site stores about you, why it stores it, who else sees it,
        and how to get rid of it. It is written to be read, not to be survived.
      </p>

      <h2>You can use most of the site without an account</h2>
      <p>
        Browsing, searching, reading quest, item, NPC, object, zone, ability and recipe pages, and
        viewing a generated build all work while logged out. None of it requires an account, and
        the site sets no cookie until you start signing in.
      </p>

      <h2>What is stored when you do sign in</h2>
      <p>
        Signing in goes through Battle.net. Blizzard tells us two things about you and nothing
        else: your <b>BattleTag</b> and your <b>Battle.net account ID</b>. We never receive your
        email address, your password, or your payment details, and we never ask you for them.
      </p>
      <p>Alongside those, your account row holds:</p>
      <ul>
        <li>the time you last signed in;</li>
        <li>your role on the site (player, moderator or administrator);</li>
        <li>your daily build allowance, if an administrator has set a custom one;</li>
        <li>if you have been banned, the reason and who did it.</li>
      </ul>

      <h2>What you choose to add</h2>
      <ul>
        <li>
          <b>Contributions.</b> Submissions, edits, comments, screenshots, votes and reports are
          stored with your BattleTag attached. Contributions other than votes and reports are
          public, and are shown with your BattleTag next to them — that attribution is the point
          of a community database.
        </li>
        <li>
          <b>Imported characters.</b> If you import characters, the site asks Blizzard for their
          name, realm, region, level, class, race, faction, gender, guild, equipment and talents,
          and stores that. <b>Every imported character is private by default.</b> A character is
          visible to anyone — and can appear in search engines — only after you switch it to
          public yourself on <Link to="/me">your page</Link>. You can switch it back, or delete
          every character you have imported, at any time.
        </li>
        <li>
          <b>Addon uploads.</b> The addon records what it sees in the game world: mob locations,
          loot, quest details, vendor prices. By design it collects no character name, realm,
          guild, chat, or account identifier. Three things about the sender do ride along, and
          none of them identify you: the <b>faction</b> of the character you were playing
          (because drop rates, vendors and quest text differ between Alliance and Horde), the
          addon version, and the game client build. The upload itself is recorded against your
          account so it can be credited and moderated. See{' '}
          <Link to="/addon">the addon page</Link> for what a file contains.
        </li>
        <li>
          <b>Builds.</b> A build stores the inputs you gave it (class, race, faction, level, role,
          gear scope, any equipped items, and the character you picked if you picked one), the
          result, and what the request cost in tokens. A build page is public at its URL.
        </li>
      </ul>

      <h2>Cookies</h2>
      <p>
        The site sets <b>two</b> cookies, both strictly necessary, both first-party:
      </p>
      <ul>
        <li>
          a <b>session cookie</b>, set when you sign in, which is how the site knows the next
          request is still you;
        </li>
        <li>
          a short-lived <b>sign-in state cookie</b>, set while you are being handed to Battle.net
          and back, which exists to stop a third party forging that round trip.
        </li>
      </ul>
      <p>
        There are no analytics cookies, no advertising cookies, no third-party trackers and no
        pixels. Nothing here follows you to another site, which is why there is no consent banner
        to click through: there is nothing to consent to beyond the cookies that make signing in
        work. Clearing them signs you out and costs you nothing else.
      </p>

      <h2>Who else sees your data</h2>
      <ul>
        <li>
          <b>Blizzard Entertainment</b> — handles the sign-in itself and supplies your BattleTag
          and any character data you import. Their handling of your Battle.net account is covered
          by Blizzard's own privacy policy, not this one.
        </li>
        <li>
          <b>Amazon Web Services</b> — hosts the site and its database, in the US East (Northern
          Virginia) region. Everything stored by the site lives there.
        </li>
        <li>
          <b>Amazon Bedrock</b> — when you generate a build, the character description you
          submitted (class, race, faction, level, role, gear scope and any items you listed) is
          sent to a large language model running in that same AWS account and region, which
          returns the build. Your BattleTag and account ID are not part of that request.
        </li>
      </ul>
      <p>
        That is the entire list. Your data is not sold, rented, traded, or handed to advertisers
        or data brokers. It would be disclosed to a law-enforcement or court order if we received
        a valid one.
      </p>

      <h2>How long it is kept</h2>
      <p>
        Account records and public contributions are kept while the site runs, because the
        database is cumulative and its edit history is what makes it trustworthy. Imported
        characters are kept until you delete them. Server request logs are short-lived
        operational records used to keep the site up and to investigate abuse.
      </p>

      <h2>What you can ask for</h2>
      <p>
        Whatever your local law gives you — and regardless of whether it gives you anything — you
        can ask us to show you what is stored about your account, correct it, or delete it. Write
        to <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> from the Battle.net account in
        question, or tell us your BattleTag, and we will answer.
      </p>
      <p>
        Two honest caveats about deletion. First, <b>public contributions are not fully
        retractable</b>: the database and its revision history are a shared record that other
        people have built on, so we will detach your BattleTag from your contributions rather
        than erase the contributions themselves. Second, <b>deleting your account does not undo
        moderation</b> — a ban record is kept so a ban cannot be shed by re-registering.
      </p>
      <p>
        You can do the most common things yourself, without asking: unpublish or delete your
        imported characters on <Link to="/me">your page</Link>, and delete your own comments.
      </p>

      <h2>Children</h2>
      <p>
        The site is not directed at children under 13 and we do not knowingly store their data. If
        you believe a child has an account here, write to us and it will be removed.
      </p>

      <h2>Security</h2>
      <p>
        Traffic is served over HTTPS. The site never holds a password of yours, because sign-in is
        Battle.net's. No service can promise perfect security, and this one does not.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes in substance, the date at the top changes with it. Continuing to
        use the site after that is how you accept the new version.
      </p>

      <h2>Contact</h2>
      <p>
        Privacy questions: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. Anything else:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </div>
  );
}
